// UX92 (docs/build-briefs/UX92-add-discount-popout.md; P0.9): the Add Discount popout.
// Built at runtime and appended to the admin screen, not written into index.html, because
// check:chrome-ratchet (ADR-004 Decisions 2 and 5) refuses a new scrim-shaped overlay there. The
// markup, ids and test ids are the same a static block would have carried, so admin.ts looks the
// pieces up exactly as before. The create route does not accept Auto-apply on membership, so it
// is not offered when adding; LOCSET1 (B9) offers it, with the scope and schedule, when the same sheet edits a discount.

const DISCOUNT_ADD_SHEET_HTML = `
<div
          class="scrim"
          id="admin-discount-add-scrim"
          data-testid="admin-discount-add-scrim"
          hidden
        >
          <div
            class="sheet admin-confirm-sheet admin-discount-add-sheet"
            id="admin-discount-add-sheet"
            data-testid="admin-discount-add-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-discount-add-title"
          >
            <div class="sheet-header">
              <div class="sheet-title-row">
                <span class="sheet-title" id="admin-discount-add-title">Add Discount</span>
                <button
                  class="btn-icon"
                  type="button"
                  id="admin-discount-add-close"
                  data-testid="admin-discount-add-close"
                  aria-label="Close without adding"
                >
                  &#10005;
                </button>
              </div>
            </div>
            <div class="sheet-body admin-form-body admin-discount-add-body">
              <label class="field-label field-label-tight" for="admin-settings-discount-name-input">Name</label>
              <input
                class="field-input"
                type="text"
                id="admin-settings-discount-name-input"
                data-testid="admin-settings-discount-name-input"
                placeholder="e.g. Happy hour"
                maxlength="60"
              />
              <label class="field-label" for="admin-settings-discount-type-input">Type</label>
              <select
                class="field-input"
                id="admin-settings-discount-type-input"
                data-testid="admin-settings-discount-type-input"
              >
                <option value="percentage">Percent off</option>
                <option value="amount">Dollars off</option>
              </select>
              <label class="field-label" for="admin-settings-discount-value-input">Amount</label>
              <input
                class="field-input"
                type="text"
                inputmode="decimal"
                id="admin-settings-discount-value-input"
                data-testid="admin-settings-discount-value-input"
              />
              <fieldset class="admin-discount-add-group">
                <legend class="field-label">Where it can be used</legend>
                <label class="admin-discount-check">
                  <input
                    type="checkbox"
                    id="admin-settings-discount-tickets-input"
                    data-testid="admin-settings-discount-tickets-input"
                    checked
                  />
                  Tickets
                </label>
                <label class="admin-discount-check">
                  <input
                    type="checkbox"
                    id="admin-settings-discount-items-input"
                    data-testid="admin-settings-discount-items-input"
                    checked
                  />
                  Items
                </label>
              </fieldset>
              <fieldset class="admin-discount-add-group">
                <legend class="field-label">Rules</legend>
                <label class="admin-discount-check">
                  <input
                    type="checkbox"
                    id="admin-settings-discount-approval-input"
                    data-testid="admin-settings-discount-approval-input"
                  />
                  Needs approval
                </label>
                <label class="admin-discount-check">
                  <input
                    type="checkbox"
                    id="admin-settings-discount-reason-input"
                    data-testid="admin-settings-discount-reason-input"
                  />
                  Needs a reason
                </label>
              </fieldset>
              <!-- LOCSET1 (B9): editing a discount reuses this sheet. The block below is drawn only while a discount is being edited:
                   the two rules the create route does not take, the scope and the schedule, all the inline row used to save. -->
              <fieldset class="admin-discount-add-group" id="admin-discount-edit-rules" data-testid="admin-discount-edit-rules" hidden>
                <legend class="field-label">More rules</legend>
                <label class="admin-discount-check">
                  <input
                    type="checkbox"
                    id="admin-settings-discount-auto-apply-input"
                    data-testid="admin-settings-discount-auto-apply-input"
                  />
                  Auto-apply on membership
                </label>
                <label class="admin-discount-check">
                  <input
                    type="checkbox"
                    id="admin-settings-discount-closes-ticket-input"
                    data-testid="admin-settings-discount-closes-ticket-input"
                  />
                  Closes the ticket
                </label>
                <p
                  class="admin-discount-check-hint"
                  id="admin-discount-closes-ticket-hint"
                  data-testid="admin-discount-closes-ticket-hint"
                  role="note"
                  hidden
                >
                  Zeroes the balance and ends the ticket immediately, before anyone can leave a tip. Only Walkout should carry this today.
                </p>
                <span
                  class="admin-discount-readonly-flag"
                  id="admin-discount-member-daily-comp"
                  data-testid="admin-discount-member-daily-comp"
                ></span>
              </fieldset>
              <div id="admin-discount-edit-scope" data-testid="admin-discount-edit-scope" hidden></div>
              <div id="admin-discount-edit-schedule" data-testid="admin-discount-edit-schedule" hidden>
                <p class="admin-discount-edit-note" id="admin-discount-edit-schedule-note">A schedule is saved with its own Save schedule button, apart from Save changes.</p>
              </div>
            </div>
            <p
              class="admin-form-error admin-discount-add-error"
              id="admin-discount-add-error"
              data-testid="admin-discount-add-error"
              role="alert"
            ></p>
            <div class="admin-confirm-actions">
              <button
                class="btn-secondary"
                type="button"
                id="admin-discount-add-cancel"
                data-testid="admin-discount-add-cancel"
              >
                Cancel
              </button>
              <button
                class="btn-primary"
                type="button"
                id="admin-settings-discount-add"
                data-testid="admin-settings-discount-add"
              >
                Add Discount
              </button>
            </div>
          </div>
        </div>
`;

/** Creates the popout (hidden) inside `parent` and returns its scrim. Call once, at load. */
export function mountDiscountAddSheet(parent: HTMLElement): HTMLElement {
  const holder = document.createElement("div");
  holder.innerHTML = DISCOUNT_ADD_SHEET_HTML.trim();
  const scrim = holder.firstElementChild;
  if (!(scrim instanceof HTMLElement)) {
    throw new Error("the Add Discount popout markup has no root element");
  }
  parent.append(scrim);
  return scrim;
}
